import React, { useState } from 'react'
import { addDonationToBasket } from '@lib/spektrix'

const FundDonation = (props) => {
  // Handle props from remount which may come through differently
  const fundId = props.fundid || props.fundId || props['fund-id'] || '';
  const fundName = props.fundname || props.fundName || props['fund-name'] || 'Donation';
  const defaultAmounts = props.defaultamounts || props.defaultAmounts || props['default-amounts'] || '[10,25,50,100,200]';
  const customAmounts = props.customamounts || props.customAmounts || props['custom-amounts'] || null;
  const [selectedAmount, setSelectedAmount] = useState(null)
  const [customAmount, setCustomAmount] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // Parse amounts if they come as JSON strings
  let parsedDefaultAmounts = defaultAmounts
  let parsedCustomAmounts = customAmounts
  
  try {
    if (typeof defaultAmounts === 'string') {
      parsedDefaultAmounts = JSON.parse(defaultAmounts)
    }
    if (typeof customAmounts === 'string' && customAmounts) {
      parsedCustomAmounts = JSON.parse(customAmounts)
    }
  } catch (e) {
    console.error('Error parsing amounts:', e)
    console.error('defaultAmounts:', defaultAmounts)
    console.error('customAmounts:', customAmounts)
  }

  // Use custom amounts for this fund if provided, otherwise use defaults
  // Ensure amounts is always an array
  const amounts = parsedCustomAmounts || parsedDefaultAmounts || [10, 25, 50, 100, 200]
  const amountsArray = Array.isArray(amounts) ? amounts : [10, 25, 50, 100, 200]
  
  console.log('Debug - amountsArray:', amountsArray) // Debug log to see what we're working with

  const handleAmountClick = amount => {
    setSelectedAmount(amount)
    setShowCustomInput(false)
    setCustomAmount('')
    setError(null)
  }

  const handleCustomClick = () => {
    setSelectedAmount(null)
    setShowCustomInput(true)
    setError(null)
  }

  const handleCustomAmountChange = e => {
    const value = e.target.value
    // Allow only numbers and decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setCustomAmount(value)
    }
  }

  const isValidForSubmit = () => {
    if (showCustomInput) {
      const amount = parseFloat(customAmount)
      return amount && amount >= 1
    }
    return selectedAmount !== null
  }

  const handleAddToBasket = async () => {
    setError(null)
    setSuccess(false)

    // Determine the amount to donate
    let amount = selectedAmount
    if (showCustomInput) {
      amount = parseFloat(customAmount)
      if (!amount || amount < 1) {
        setError('Please enter a valid amount (minimum £1)')
        return
      }
    } else if (!amount) {
      setError('Please select an amount')
      return
    }

    setLoading(true)

    try {
      // Create donation object
      const donation = {
        fundId: fundId,
        amount: amount // Amount in pounds
      }

      await addDonationToBasket(donation)
      setSuccess(true)

      // Show success message briefly then redirect
      setTimeout(() => {
        window.location.href = '/basket'
      }, 2000)
    } catch (error) {
      console.error('Error adding donation to basket:', error)
      if (error.message) {
        setError(error.message)
      } else if (error.errors && error.errors.length > 0) {
        setError(error.errors[0].message)
      } else {
        setError('Failed to add donation to basket. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-2xl mx-auto p-6">
      <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
        Make a donation to {fundName}
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        {amountsArray.map(amount => (
          <button
            key={amount}
            onClick={() => handleAmountClick(amount)}
            className={`p-4 rounded-lg font-semibold text-lg transition-all ${
              selectedAmount === amount && !showCustomInput
                ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            disabled={loading}
          >
            £{amount}
          </button>
        ))}
        <button
          onClick={handleCustomClick}
          className={`p-4 rounded-lg font-semibold text-lg transition-all ${
            showCustomInput
              ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}
          disabled={loading}
        >
          Custom
        </button>
      </div>

      {showCustomInput && (
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Enter custom amount
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 dark:text-gray-400">
              £
            </span>
            <input
              type="text"
              value={customAmount}
              onChange={handleCustomAmountChange}
              placeholder="0.00"
              className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              disabled={loading}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-200 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-600 text-green-700 dark:text-green-200 rounded-lg">
          Donation added to basket! Redirecting...
        </div>
      )}

      <div className="flex justify-center">
        <button
          onClick={handleAddToBasket}
          disabled={loading || !isValidForSubmit()}
          className={`w-full max-w-sm py-3 px-6 rounded-lg font-semibold transition-all ${
            loading || !isValidForSubmit()
              ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {loading ? 'Adding...' : 'Add to Basket'}
        </button>
      </div>
    </div>
  )
}

export default FundDonation